import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useToast } from "@/hooks/use-toast"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { BillsTable } from '@/components/BillsTable'
import { BillImpactDialog } from '@/components/BillImpactDialog'
import { Eye, EyeOff, AlertTriangle } from "lucide-react"
import { useBillsWorkflow } from '@/hooks/bills/useBillsWorkflow'
import { useConfig } from '@/hooks/config/useConfig'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { SessionDisplay } from '@/components/SessionDisplay'
import type { BillWorkflowStatus, BillAction, Bill } from '@/types/bill'
import type { APISession } from '@/types/session'

const API_SESSIONS: APISession[] = ['2023RS', '2024RS', '2025RS']

const WORKFLOW_TABS = [
  { value: 'New', label: 'New', description: 'New bills requiring review', icon: Eye },
  { value: 'Monitoring', label: 'Monitoring', description: 'Bills being monitored', icon: Eye },
  { value: 'Ignored', label: 'Ignored', description: 'Bills not relevant', icon: EyeOff },
  { value: 'Full Text Unavailable', label: 'Full Text Unavailable', description: 'Bills missing full text', icon: AlertTriangle }
] as const

export default function BillsPage() {
  const { toast } = useToast()
  const { config } = useConfig()
  const [selectedBillForAnalysis, setSelectedBillForAnalysis] = useState<Bill | null>(null)

  const [initialActiveTab] = useState<BillWorkflowStatus>(() => {
    if (typeof window !== 'undefined') {
      const savedTab = localStorage.getItem("billsTab");
      const validTabs: BillWorkflowStatus[] = ["New", "Monitoring", "Ignored", "Full Text Unavailable"];
      if (savedTab && validTabs.includes(savedTab as BillWorkflowStatus)) {
        return savedTab as BillWorkflowStatus;
      }
    }
    return "New";
  });


  const {
    bills,
    isLoading,
    selectedBills,
    activeTab,
    selectedSession,
    setSelectedSession,
    setActiveTab,
    handleSelectBill,
    handleSelectAll,
    updateBillsWorkflow,
    processBillAction,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    isFetchingNextPage,
    processingActions,
    sortField,
    sortDirection,
    setSorting
  } = useBillsWorkflow(initialActiveTab, config?.ActiveSession)

  useEffect(() => {
    if (activeTab) {
      localStorage.setItem("billsTab", activeTab)
    }
  }, [activeTab])

  const handleSessionChange = (value: string) => {
    if (API_SESSIONS.includes(value as APISession)) {
      setSelectedSession(value as APISession)
    }
  }

  const handleMonitorBills = async (billIds: string[]) => {
    try {
      const count = await updateBillsWorkflow(billIds, 'Monitoring')
      toast({
        title: "Bills Marked for Monitoring",
        description: `${count} bill(s) moved to monitoring.`
      })
    } catch (error) {
      console.error("Error in handleMonitorBills:", error);
      toast({
        title: "Action Failed",
        description: "There was an error updating the bills.",
        variant: "destructive"
      })
    }
  }

  const handleIgnoreBills = async (billIds: string[]) => {
    try {
      const count = await updateBillsWorkflow(billIds, 'Ignored')
      toast({
        title: "Bills Ignored",
        description: `${count} bill(s) marked as ignored.`
      })
    } catch (error) {
      console.error("Error in handleIgnoreBills:", error);
      toast({
        title: "Action Failed",
        description: "There was an error updating the bills.",
        variant: "destructive"
      })
    }
  }

  const handleBillAction = async (billId: string, action: BillAction, allBillIds?: string[]) => {
    try {
      const billsToProcess = allBillIds || [billId];
      await processBillAction(billsToProcess, action)
      
      // Only show notification for single bills (bulk actions handled separately)
      if (!allBillIds) {
        toast({
          title: `Action '${action}' Started`,
          description: `Processing started for bill ${billId}`
        })
      }
    } catch (error) {
      console.error("Error in handleBillAction:", error);
      toast({
        title: "Action Failed",
        description: "There was an error processing the bill(s).",
        variant: "destructive"
      })
    }
  }

  if (isLoading && !selectedSession) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex justify-center items-center h-64">
            Loading...
          </CardContent>
        </Card>
      </div>
    )
  }

  const currentTab = WORKFLOW_TABS.find(tab => tab.value === activeTab)

  return (
    <div className="container mx-auto p-2 sm:p-4 md:p-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
              <div>
                <CardTitle>Legislative Bills</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {currentTab?.description}
                </p>
              </div>
              <div className="flex gap-4 items-center w-full sm:w-auto">
                <Select
                  value={selectedSession || undefined}
                  onValueChange={handleSessionChange}
                >
                  <SelectTrigger className="w-[200px] sm:w-[200px]">
                    <SelectValue placeholder="Select session">
                      {selectedSession && <SessionDisplay session={selectedSession} />}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {API_SESSIONS.map((session) => (
                      <SelectItem key={session} value={session}>
                        <SessionDisplay session={session} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
              value={activeTab}
              className="space-y-4"
              onValueChange={(value) => setActiveTab(value as BillWorkflowStatus)}
          >
            <div className="overflow-auto">
              <TabsList className="w-fit">
                {WORKFLOW_TABS.map((tab) => (
                    <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className="gap-2 text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium"
                    >
                      <tab.icon className="h-4 w-4"/>
                      {tab.label}
                    </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {WORKFLOW_TABS.map((tab) => (
              <TabsContent key={tab.value} value={tab.value}>
                <BillsTable
                  bills={bills}
                  selectedBills={selectedBills}
                  currentTab={activeTab}
                  onSelectBill={handleSelectBill}
                  onSelectAll={handleSelectAll}
                  onMonitorBill={(billId, allBillIds) => handleMonitorBills(allBillIds || [billId])}
                  onIgnoreBill={(billId, allBillIds) => handleIgnoreBills(allBillIds || [billId])}
                  onProcessAction={handleBillAction}
                  onShowImpactDetails={(bill) => setSelectedBillForAnalysis(bill)}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  isLoading={isFetchingNextPage}
                  processingActions={processingActions}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSortChange={setSorting}
                  session={selectedSession}
                />
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <BillImpactDialog
        bill={selectedBillForAnalysis}
        open={!!selectedBillForAnalysis}
        onOpenChange={(open) => !open && setSelectedBillForAnalysis(null)}
      />
    </div>
  )
}